import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { Toaster } from '@/components/ui/toaster';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';

import { AppShell } from '@/components/layout/AppShell';
import { LandingPage } from '@/routes/landing/LandingPage';
import { LoginPage } from '@/routes/auth/LoginPage';
import { SignupPage } from '@/routes/auth/SignupPage';
import { ForgotPasswordPage } from '@/routes/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/routes/auth/ResetPasswordPage';
import { PendingApprovalPage } from '@/routes/auth/PendingApprovalPage';
import { AccountBlockedPage } from '@/routes/auth/AccountBlockedPage';
import { AcceptInvitationPage } from '@/routes/auth/AcceptInvitationPage';
import { PlaceholderPage } from '@/routes/PlaceholderPage';
import { DashboardPage } from '@/routes/dashboard/DashboardPage';
import { GroupsPage } from '@/routes/groups/GroupsPage';
import { GroupDetailPage } from '@/routes/groups/GroupDetailPage';
import { GoalDetailPage } from '@/routes/goals/GoalDetailPage';
import { SprintDetailPage } from '@/routes/sprints/SprintDetailPage';
import { TeamDetailPage } from '@/routes/teams/TeamDetailPage';
import { TasksPage } from '@/routes/tasks/TasksPage';
import { TaskDetailPage } from '@/routes/tasks/TaskDetailPage';
import { WorkPage } from '@/routes/work/WorkPage';
import { WorkspacesPage } from '@/routes/workspaces/WorkspacesPage';
import { UsersPage } from '@/routes/admin/UsersPage';
import { UserDetailPage } from '@/routes/admin/UserDetailPage';
import { SystemOverviewPage } from '@/routes/admin/SystemOverviewPage';
import { AuditLogsPage } from '@/routes/admin/AuditLogsPage';
import { ErrorLogsPage } from '@/routes/admin/ErrorLogsPage';
import { TimerLogsPage } from '@/routes/admin/TimerLogsPage';

import {
  Users, TrendingUp, Star, BarChart3, User, Bell,
} from 'lucide-react';

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            {/* Public */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/pending-approval" element={<PendingApprovalPage />} />
            <Route path="/blocked" element={<AccountBlockedPage />} />
            <Route path="/accept-invitation" element={<AcceptInvitationPage />} />

            {/* Protected */}
            <Route element={<AppShell />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/work" element={<WorkPage />} />
              <Route path="/groups" element={<GroupsPage />} />
              <Route path="/groups/:id" element={<GroupDetailPage />} />
              <Route path="/goals/:id" element={<GoalDetailPage />} />
              <Route path="/sprints/:id" element={<SprintDetailPage />} />
              <Route path="/teams/:id" element={<TeamDetailPage />} />
              <Route path="/tasks" element={<TasksPage />} />
              <Route path="/tasks/:id" element={<TaskDetailPage />} />
              <Route path="/workspaces" element={<WorkspacesPage />} />
              <Route path="/people" element={<PlaceholderPage title="People" description="Workspace members" icon={Users} />} />
              <Route path="/performance" element={<PlaceholderPage title="Performance" description="Scores & rankings" icon={TrendingUp} />} />
              <Route path="/performance/:userId" element={<PlaceholderPage title="User Performance" icon={TrendingUp} />} />
              <Route path="/evaluate" element={<PlaceholderPage title="Evaluate" description="Pending evaluations" icon={Star} />} />
              <Route path="/reports" element={<PlaceholderPage title="Reports" description="Analytics & insights" icon={BarChart3} />} />
              <Route path="/profile" element={<PlaceholderPage title="Profile" description="Your account settings" icon={User} />} />
              <Route path="/notifications" element={<PlaceholderPage title="Notifications" icon={Bell} />} />
              <Route path="/admin/users" element={<UsersPage />} />
              <Route path="/admin/users/:id" element={<UserDetailPage />} />
              <Route path="/admin/system-overview" element={<SystemOverviewPage />} />
              <Route path="/admin/audit-logs" element={<AuditLogsPage />} />
              <Route path="/admin/timer-logs" element={<TimerLogsPage />} />
              <Route path="/admin/error-logs" element={<ErrorLogsPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
